import { FC, useMemo, useRef, useState } from "react";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { useNavigate, useParams } from "react-router-dom";
import { config } from "../config";
import { useApiClient } from "@hostingbv/lib/src/api";
import { useFetchedResource } from "@hostingbv/lib/src/util/useFetchedResource";
import { Domain } from "@hostingbv/lib/src/api/partner/models/Domain";
import { useRefreshEffect } from "@hostingbv/lib/src/components/RefreshController";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { Loading } from "@hostingbv/lib/src/components/Loading";
import { usePermissions } from "@hostingbv/lib/src/auth/permissions";
import { EntityNotesButton } from "../components/EntityNotesButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SectionHeader } from "../../../lib/src/components/content/SectionHeader";
import { Card } from "../../../lib/src/components/Card";
import { Button, IconButton } from "../../../lib/src/components/form/Button";
import { APIResponse } from "../../../lib/src/api/ModelFactory";
import { DNSZone } from "../../../lib/src/api/partner/models/DNSZone";
import { Editor } from "../../../lib/src/components/dns-editor/Editor";
import { faDownload, faExclamationTriangle, faGlobe, faTimes, faUpload } from "@fortawesome/free-solid-svg-icons";
import { PageIconHeader } from "../../../lib/src/components/PageIconHeader";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";
import { Paragraph } from "../../../lib/src/components/Paragraph";

export const DomainDnsImport: FC = () => {
  const { uuid } = useParams()
  const { partnerClient } = useApiClient(config)
  const { resource, reload } = useFetchedResource(() => partnerClient.getDomain(uuid ?? ""))
  const domain: Domain | null = useMemo(() => resource?.success === true ? resource.data : null, [resource])

  useRefreshEffect(() => { reload() })
  const { can } = usePermissions(partnerClient)
  return (
    <ContentContainer>
      <PageIconHeader icon={faGlobe}>
        <Breadcrumbs currentPage={'DNS Zone Import'} crumbs={[
          { label: "Domains", href: "/domains" },
          { label: domain?.displayName ?? "Domain", href: domain ? `/domains/${domain.id}` : '/domains' },
        ]} />
        <PageHeader>{
          domain === null ? <span className={"block h-8 w-36 rounded-lg bg-slate-200 animate-pulse"}></span> : domain.displayName

        }<EntityNotesButton entity={domain} /></PageHeader>
      </PageIconHeader>

      <div className={"border border-yellow-600 bg-yellow-50 rounded-lg p-4"}>
        <div className={"flex items-center"}>
          <div className={"h-12 w-12 rounded-full bg-yellow-200 flex items-center justify-center"}>
            <FontAwesomeIcon icon={faExclamationTriangle} className={"text-xl text-yellow-700"} />
          </div>
          <div className={"flex-1 mx-6 flex flex-col gap-2"}>
            <h2 className={"font-medium text-lg"}>Warning: This will overwrite your current records</h2>
            <p className={"text-slate-800 text-sm max-w-2xl"}>
              After importing your zone file, you will be shown a preview of the imported records. If you continue, your current records will be overwritten with the imported records.
            </p>
            <p className={"text-slate-800 text-sm max-w-2xl"}>
              If you currently have records in your zone, we highly recommend making a backup using the export function before importing a new zone.
            </p>
          </div>
        </div>
      </div>

      <Loading loading={domain === null}>
        {domain !== null && can('edit', 'dns', domain?.permissions) && <>
          {!domain.externalDns && <Step0 domain={domain} />}
          <Step1 domain={domain} />
        </>}
      </Loading>

    </ContentContainer>
  );
}

const Step0: FC<{ domain: Domain }> = ({ domain }) => {
  const { partnerClient } = useApiClient(config)
  const { can } = usePermissions(partnerClient)
  const filename = `${domain.displayName}.zone.txt`
  const exportZone = async () => {
    if (domain === null) {
      return
    }
    const content = await partnerClient.exportDnsZone(domain.id)
    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = window.document.createElement('a')
    a.setAttribute('href', url)
    a.setAttribute('download', filename)
    a.click()
  }
  return <div className={"my-8"}>
    <SectionHeader>First: Create a backup</SectionHeader>
    <Card title={'Export zone file'}>
      <Button type={"primary"} size={'sm'} text={filename} icon={faDownload} onClick={exportZone} disabled={domain?.externalDns || !can('view', 'dns', domain.permissions)} />
    </Card>
  </div>
}
const Step1: FC<{ domain: Domain }> = ({ domain }) => {
  const { partnerClient } = useApiClient(config)
  const ref = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [zone, setZone] = useState<APIResponse<DNSZone>>()
  const navigate = useNavigate()
  const { runningDeployments } = useRealtimeDeployments()!
  const isDomainDeploying = runningDeployments.some(d => d.entityId === domain.id)
  const dryRun = async (file: File) => {
    if (domain === null || file === null) {
      return
    }
    setLoading(true)

    const result = await partnerClient.validateImportDnsZone(domain.id, file)
    setZone(result)
    setLoading(false)
  }
  const onUpload = async () => {
    const file = ref.current?.files?.[0]
    if (!file) {
      return
    }
    setFile(file)
    await dryRun(file)
  }
  const doImport = async (file: File) => {
    await partnerClient.importDnsZone(domain.id, file)
    navigate('/domains/' + domain.id + '/dns')
  }
  const errors = zone && !zone.success ? zone.errors : []
  const records = zone?.success ? zone.data.records.map((r, k) => ({ ...r, id: String(k) })) : []

  return <>
    <div className={"my-8"}>
      <SectionHeader>Next: Import your zone file</SectionHeader>
      <Card title={'Upload zone file'}>
        {file === null ? <div>
          <input type="file" className={""} ref={ref} onChange={() => onUpload()} />
        </div> : <div className={"flex items-center"}>
          <span className={"font-medium mr-2"}>{file.name}</span>
          <IconButton type={'danger'} size={'sm'} icon={faTimes} onClick={() => setFile(null)} />
        </div>}
      </Card>
    </div>
    {file !== null && <div className={"my-8"}><Loading loading={loading}>
      <SectionHeader>Validate and confirm</SectionHeader>
      <Card title={'Validate'}>
        {(errors.length) > 0 && <div className={"flex flex-col space-y-1"}>
          {errors.map((error, k) => <div key={k} className={"text-red-700 text-sm flex p-1 items-center"}>
            <FontAwesomeIcon icon={faExclamationTriangle} className={"text-red-500 mr-2"} />
            <div>{error}</div>
          </div>)}
          {errors.length > 0 && <div className={"text-red-700 text-sm p-1"}>
            Please fix these issues with your zonefile and upload again.
          </div>}
        </div>}
        {zone && zone.success && <>
          <Editor domain={domain} records={records} readonlyMode={true} onSave={() => { }} />
          <div className={"flex flex-col items-stretch mt-4"}>
            {isDomainDeploying && <Paragraph>You cannot import a zone while a deployment is still busy. Wait a bit for the deployment to finish and then proceed.</Paragraph>}
            <Button type={"primary"} size={"sm"} text={"Import these records"} icon={faUpload} disabled={isDomainDeploying} onClick={() => doImport(file)} />
          </div>
        </>}
      </Card>
    </Loading>
    </div>}
  </>
}
